/**
 * Bounded retry for post-approval party entry.
 *
 * Host Allow writes participants + approved status atomically, but the guest's
 * event getDoc can briefly lag the joinRequest listener. joinParty then returns
 * { pending: true, joinStatus: 'approved' } and a single attempt never retries.
 */

export const APPROVED_JOIN_MAX_ATTEMPTS = 6;
export const APPROVED_JOIN_RETRY_DELAY_MS = 350;

export function isApprovedSoftWait(result) {
  return Boolean(result?.pending && result?.joinStatus === 'approved');
}

export function sleepMs(ms, { setTimeoutFn = setTimeout } = {}) {
  return new Promise((resolve) => {
    setTimeoutFn(resolve, ms);
  });
}

/**
 * Retry joinParty while the server says "approved but participants not visible".
 * Stops on success, hard failure, non-approved pending, or attempt budget.
 */
export async function enterPartyAfterApproval({
  joinParty,
  eventCode,
  displayName = '',
  maxAttempts = APPROVED_JOIN_MAX_ATTEMPTS,
  delayMs = APPROVED_JOIN_RETRY_DELAY_MS,
  sleep = sleepMs,
} = {}) {
  if (typeof joinParty !== 'function') {
    throw new Error('joinParty is required');
  }

  let lastError = null;
  let lastResult = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const result = await joinParty({ eventCode, displayName });
      lastResult = result;
      if (!result?.pending) {
        return { ok: true, result, attempts: attempt };
      }
      if (!isApprovedSoftWait(result)) {
        return { ok: false, result, attempts: attempt, reason: 'not-approved-ready' };
      }
      if (attempt < maxAttempts) {
        await sleep(delayMs);
      }
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        await sleep(delayMs);
        continue;
      }
      return { ok: false, error: lastError, attempts: attempt, reason: 'error' };
    }
  }

  return {
    ok: false,
    result: lastResult,
    error: lastError,
    attempts: maxAttempts,
    reason: lastError ? 'error' : 'retry-exhausted',
  };
}
